<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import HotelPortalAPI from 'dashboard/api/hotelPortal';
import { mealInfo, getView, VIEW_I18N_KEY, SOURCE_META, fP, fN } from 'dashboard/helper/hotelPortalHelpers';

const SOURCE_STYLES = {
  vi: {
    top: 'border-t-violet-500',
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
    dot: 'bg-violet-500',
  },
  ma: {
    top: 'border-t-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  an: {
    top: 'border-t-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  bb: {
    top: 'border-t-cyan-500',
    badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
    dot: 'bg-cyan-500',
  },
};

const MEAL_STYLES = {
  ro: 'bg-n-slate-3 text-n-slate-11',
  bb: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  hb: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  fb: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  ai: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
};

export default {
  components: { NextButton },
  props: {
    result: { type: Object, required: true },
    city: { type: String, default: '' },
  },
  data() {
    return {
      isCheckingCompetitor: false,
      competitorResult: null,
      competitorError: '',
    };
  },
  computed: {
    sourceKey() {
      return SOURCE_META[this.result.source]?.key || 'ma';
    },
    sourceMetaEntry() {
      return SOURCE_META[this.result.source];
    },
    sourceLabel() {
      return this.sourceMetaEntry ? this.$t(this.sourceMetaEntry.i18nKey) : this.result.source;
    },
    sourceStyle() {
      return SOURCE_STYLES[this.sourceKey] || SOURCE_STYLES.ma;
    },
    meal() {
      return mealInfo(this.result.meal);
    },
    mealLabel() {
      return this.$t(this.meal.i18nKey);
    },
    mealStyle() {
      return MEAL_STYLES[this.meal.cls] || MEAL_STYLES.ro;
    },
    viewKey() {
      return getView(this.result.room_type, this.result.hotel_name);
    },
    viewLabel() {
      return this.viewKey ? this.$t(VIEW_I18N_KEY[this.viewKey]) : null;
    },
    boardsEntries() {
      const boards = this.result.boards_ar;
      return boards && Object.keys(boards).length > 1 ? Object.entries(boards) : [];
    },
    stars() {
      return this.result.stars || 0;
    },
    starText() {
      return '★'.repeat(Math.min(this.stars, 5));
    },
    copyText() {
      const r = this.result;
      const name = r.hotel_name || r.hotel_name_en;
      const cancel = r.free_cancellation ? ' — إلغاء مجاني' : '';
      return `${name} — ${r.room_type || 'غرفة'} (${r.meal || ''}) — ${r.per_night} ر.س/ليلة — الإجمالي: ${r.total} ر.س لـ ${r.nights || '—'} ليالٍ${cancel}`;
    },
  },
  methods: {
    fP,
    fN,
    async copyPrice() {
      try {
        await navigator.clipboard.writeText(this.copyText);
        useAlert(this.$t('HOTEL_PORTAL.COPIED'));
      } catch (error) {
        useAlert(this.$t('HOTEL_PORTAL.COPY_FAILED'));
      }
    },
    async checkCompetitor() {
      this.isCheckingCompetitor = true;
      this.competitorError = '';
      this.competitorResult = null;
      try {
        const hotelName = this.result.hotel_name_en || this.result.hotel_name || '';
        const { data } = await HotelPortalAPI.competitorCheck({
          hotel: hotelName,
          city: this.city || 'Makkah',
          check_in: this.result.check_in,
          check_out: this.result.check_out,
        });
        if (!data.ok) {
          this.competitorError = data.error || this.$t('HOTEL_PORTAL.COMPETITOR_FAILED');
          return;
        }
        this.competitorResult = data;
      } catch (error) {
        this.competitorError = this.$t('HOTEL_PORTAL.COMPETITOR_FAILED');
      } finally {
        this.isCheckingCompetitor = false;
      }
    },
    sortedOffers() {
      return (this.competitorResult?.offers || [])
        .slice()
        .sort((a, b) => (a.price_sar || 0) - (b.price_sar || 0));
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col overflow-hidden bg-n-solid-2 border border-n-weak rounded-xl border-t-4 shadow-sm hover:shadow-md transition-shadow"
    :class="sourceStyle.top"
  >
    <div class="bg-n-solid-12 dark:bg-n-slate-3 px-3.5 pt-3 pb-2.5">
      <span
        class="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5"
        :class="sourceStyle.badge"
      >
        <span class="w-1.5 h-1.5 rounded-full" :class="sourceStyle.dot" />
        {{ sourceLabel }}
      </span>
      <div class="text-sm font-bold text-white leading-snug">
        {{ result.hotel_name || result.hotel_name_en }}
      </div>
      <div
        v-if="result.hotel_name_en && result.hotel_name_en !== result.hotel_name"
        class="text-[10.5px] text-white/50"
      >
        {{ result.hotel_name_en }}
      </div>
      <div v-if="stars > 0" class="text-[11px] text-amber-400 mt-1">
        {{ starText }}
        <span class="text-[10px] text-white/60">{{ $t('HOTEL_PORTAL.CARD.STARS', { count: stars }) }}</span>
      </div>
      <div v-else class="text-[10.5px] text-white/40 mt-1">{{ $t('HOTEL_PORTAL.CARD.UNRATED') }}</div>
      <div
        v-if="result.zone_name_ar"
        class="text-[10px] text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 mt-1.5 inline-block"
        :title="result.zone_sales_note || ''"
      >
        📍 {{ result.zone_name_ar }} ({{ $t('HOTEL_PORTAL.CARD.ZONE_PRIORITY', { priority: result.zone_priority }) }})
      </div>
    </div>

    <div class="p-3.5 flex-1 flex flex-col gap-2.5">
      <div class="flex items-center gap-1.5 flex-wrap">
        <span v-if="result.room_type" class="text-[13px] font-semibold text-n-slate-12">
          {{ result.room_type }}
        </span>
        <span v-else class="text-[11.5px] text-n-slate-11">{{ $t('HOTEL_PORTAL.CARD.ROOM_UNAVAILABLE') }}</span>
        <span
          v-if="viewLabel"
          class="inline-flex text-[11px] px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11 border border-n-weak"
        >
          {{ viewLabel }}
        </span>
        <span class="inline-flex text-[11.5px] px-2 py-0.5 rounded-full font-medium" :class="mealStyle">
          {{ mealLabel }}
        </span>
      </div>

      <div v-if="boardsEntries.length" class="flex flex-wrap gap-1.5">
        <span
          v-for="[label, price] in boardsEntries"
          :key="label"
          class="text-[11px] px-2 py-0.5 rounded-lg bg-n-slate-3 border border-n-weak text-n-slate-11"
        >
          {{ label }}: <b>{{ fP(price) }}</b> {{ $t('HOTEL_PORTAL.CURRENCY') }}
        </span>
      </div>

      <div class="flex items-end justify-between py-2.5 border-y border-n-weak">
        <div class="flex flex-col">
          <div v-if="sourceKey !== 'vi' && sourceKey !== 'an'" class="text-[9.5px] text-emerald-600 font-bold uppercase tracking-wide mb-0.5">
            {{ $t('HOTEL_PORTAL.CARD.FROM') }}
          </div>
          <div class="text-[26px] font-extrabold text-amber-600 leading-none tabular-nums">
            {{ fP(result.per_night) }}
          </div>
          <div class="text-[10px] text-n-slate-11 mt-0.5">{{ $t('HOTEL_PORTAL.CARD.PER_NIGHT') }}</div>
        </div>
        <div class="flex flex-col items-end">
          <span class="text-[11px] bg-n-slate-3 text-n-slate-11 rounded px-2 py-0.5 mb-1.5">
            {{ $t('HOTEL_PORTAL.CARD.NIGHTS', { count: result.nights || 0 }) }}
          </span>
          <div class="text-sm font-bold text-n-slate-12 tabular-nums">{{ fP(result.total) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</div>
          <div class="text-[10px] text-n-slate-11">{{ $t('HOTEL_PORTAL.CARD.TOTAL_STAY') }}</div>
        </div>
      </div>

      <div class="flex items-center justify-between flex-wrap gap-1">
        <div class="flex items-center gap-1 text-xs font-semibold text-emerald-600">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {{ $t('HOTEL_PORTAL.CARD.AVAILABLE') }}
          <span v-if="result.stock != null" class="text-[10.5px] text-n-slate-11 font-normal">
            · {{ $t('HOTEL_PORTAL.CARD.UNITS', { count: fN(result.stock) }) }}
          </span>
        </div>
        <div class="text-right">
          <div v-if="result.free_cancellation" class="text-[10.5px] text-emerald-600 font-semibold">
            {{ $t('HOTEL_PORTAL.CARD.FREE_CANCELLATION') }}
          </div>
          <div v-if="result.district" class="text-[10.5px] text-n-slate-11">
            📍 {{ result.district }}<template v-if="result.distance"> · {{ $t('HOTEL_PORTAL.CARD.DISTANCE_KM', { distance: result.distance }) }}</template>
          </div>
        </div>
      </div>

      <div v-if="result.net_price != null" class="p-2 rounded-lg bg-n-slate-3 border border-dashed border-n-weak text-[11px]">
        <div class="flex justify-between py-0.5 text-n-slate-11">
          <span>{{ $t('HOTEL_PORTAL.CARD.ORIGINAL_PRICE_VENDOR') }}</span>
          <b class="text-n-slate-12">{{ fP(result.net_price) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</b>
        </div>
        <div class="flex justify-between py-0.5 text-n-slate-11">
          <span>
            {{ result.margin_minimum
              ? $t('HOTEL_PORTAL.CARD.MARGIN_WITH_MIN', { percent: result.margin_percent != null ? Number(result.margin_percent).toFixed(1) : '—', amount: fP(result.margin_minimum) })
              : $t('HOTEL_PORTAL.CARD.MARGIN', { percent: result.margin_percent != null ? Number(result.margin_percent).toFixed(1) : '—' }) }}
          </span>
          <b class="text-amber-600">+{{ fP(result.margin_amount) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</b>
        </div>
        <div class="flex justify-between pt-1 mt-0.5 border-t border-n-weak">
          <span class="text-n-slate-12 font-medium">{{ $t('HOTEL_PORTAL.CARD.FINAL_PRICE_CUSTOMER') }}</span>
          <b class="text-n-slate-12 text-sm">{{ fP(result.total) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</b>
        </div>
      </div>
    </div>

    <div class="px-3.5 pb-3.5 flex flex-col gap-1.5">
      <NextButton
        :label="$t('HOTEL_PORTAL.COPY_PRICE')"
        sm
        faded
        amber
        class="w-full"
        @click="copyPrice"
      />
      <NextButton
        :label="$t('HOTEL_PORTAL.CHECK_COMPETITOR')"
        sm
        faded
        blue
        :is-loading="isCheckingCompetitor"
        class="w-full"
        @click="checkCompetitor"
      />
    </div>

    <div v-if="competitorError" class="mx-3.5 mb-3.5 p-2.5 rounded-lg bg-n-ruby-3 text-n-ruby-11 text-[13px]">
      ⚠️ {{ competitorError }}
    </div>
    <div v-else-if="competitorResult" class="mx-3.5 mb-3.5 p-2.5 rounded-lg bg-n-slate-3 border border-n-weak text-[13px]">
      <div class="font-bold text-n-slate-12 mb-1.5 text-xs leading-relaxed">
        {{ $t('HOTEL_PORTAL.COMPETITOR_TITLE') }}
      </div>
      <div class="text-n-slate-12 mb-2 font-semibold text-[13.5px]">
        {{ $t('HOTEL_PORTAL.COMPETITOR_MIN_PRICE') }}: <b>{{ fP(competitorResult.min_sar) }}</b> {{ $t('HOTEL_PORTAL.CURRENCY') }}
        · {{ $t('HOTEL_PORTAL.COMPETITOR_MEDIAN') }}:
        <b>{{ fP(competitorResult.median_sar) }}</b> {{ $t('HOTEL_PORTAL.CURRENCY') }}
      </div>
      <div class="max-h-60 overflow-y-auto">
        <div
          v-for="(offer, idx) in sortedOffers()"
          :key="idx"
          class="flex justify-between items-center gap-2 py-1.5 border-t border-n-weak text-n-slate-11 text-[13px]"
        >
          <span class="truncate">{{ offer.ota }}</span>
          <b class="text-n-slate-12 flex-shrink-0 text-[13.5px]">{{ fP(offer.price_sar) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</b>
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-n-weak text-[11.5px] text-n-slate-11">
        {{ $t('HOTEL_PORTAL.COMPETITOR_CREDITS_USED', { count: competitorResult.credits_charged || 0 }) }}
        <template v-if="competitorResult.real_credits_remaining != null">
          {{ $t('HOTEL_PORTAL.COMPETITOR_CREDITS_REMAINING', { count: competitorResult.real_credits_remaining }) }}
        </template>
      </div>
    </div>
  </div>
</template>
